import { z } from 'zod';
import { uuid, isoDateTime } from './common.js';
/** Phase 2, P2.1 (docs/design/10): catalogue owned here, and each shop's storefront set-up. */
export const LANGUAGES = ['da', 'en'];
export const language = z.enum(LANGUAGES);
/** Text per language; Danish is the base and the fallback. */
export const i18n = z.object({ da: z.string().max(20000).default(''), en: z.string().max(20000).default('') }).partial();
export const pick = (t, lang) => (t ? (t[lang] || t.da || '') : '');
export const imageDto = z.object({ id: uuid, width: z.number().int(), height: z.number().int(), url: z.string(), thumbUrl: z.string() });
export const videoDto = z.object({ id: uuid, url: z.string(), contentType: z.string(), sizeBytes: z.number().int() });
// --- catalogue -------------------------------------------------------------------------------------
export const vendorDto = z.object({ id: uuid, name: z.string(), slug: z.string(), active: z.boolean(), productCount: z.number().int() });
export const saveVendorRequest = z.object({ name: z.string().trim().min(1).max(200), active: z.boolean().default(true) });
export const categoryDto = z.object({
    id: uuid,
    parentId: uuid.nullable(),
    slug: z.string(),
    name: i18n,
    description: i18n,
    image: imageDto.nullable(),
    sortOrder: z.number().int(),
    active: z.boolean(),
    productCount: z.number().int(),
});
export const saveCategoryRequest = z.object({
    parentId: uuid.nullable().optional(),
    slug: z.string().trim().max(120).optional(),
    name: i18n,
    description: i18n.optional(),
    imageId: uuid.nullable().optional(),
    sortOrder: z.number().int().default(100),
    active: z.boolean().default(true),
});
export const stepPrice = z.object({ minQty: z.number().int().min(2), priceMinor: z.number().int().min(0) });
export const variantEditDto = z.object({
    id: uuid.nullable(),
    sku: z.string().trim().min(1).max(60),
    title: z.string().trim().max(200).default(''),
    options: z.record(z.string(), z.string()).default({}),
    priceMinor: z.number().int().min(0).nullable(),
    compareAtMinor: z.number().int().min(0).nullable().optional(),
    costMinor: z.number().int().min(0).nullable().optional(),
    b2cPriceMinor: z.number().int().min(0).nullable().optional(),
    stepPrices: z.array(stepPrice).max(20).default([]),
    minQty: z.number().int().min(1).default(1),
    maxQty: z.number().int().min(1).nullable().optional(),
    stepQty: z.number().int().min(1).default(1),
    /** The only quantities that can be ordered (3x's "steps"); empty = any within min, max and step. */
    allowedQty: z.array(z.number().int().min(1)).max(500).default([]),
    barcode: z.string().trim().max(40).nullable().optional(),
    weightG: z.number().int().min(0).nullable().optional(),
    lengthMm: z.number().int().min(0).nullable().optional(),
    widthMm: z.number().int().min(0).nullable().optional(),
    heightMm: z.number().int().min(0).nullable().optional(),
    hsCode: z.string().trim().max(20).nullable().optional(),
    countryOfOrigin: z.string().trim().length(2).nullable().optional(),
    customsValueMinor: z.number().int().min(0).nullable().optional(),
    active: z.boolean().default(true),
});
export const productListItemDto = z.object({
    id: uuid,
    number: z.string().nullable(),
    title: z.string(),
    vendorName: z.string().nullable(),
    image: imageDto.nullable(),
    imageUrl: z.string().nullable(),
    variantCount: z.number().int(),
    priceFromMinor: z.number().int().nullable(),
    active: z.boolean(),
    ownedHere: z.boolean(),
    shopCount: z.number().int(),
    updatedAt: isoDateTime,
});
export const productDetailDto = z.object({
    id: uuid,
    externalId: z.string().nullable(),
    number: z.string().nullable(),
    slug: z.string().nullable(),
    title: i18n,
    description: i18n,
    vendorId: uuid.nullable(),
    tags: z.array(z.string()),
    active: z.boolean(),
    ownedHere: z.boolean(),
    imageUrl: z.string().nullable(),
    images: z.array(imageDto.extend({ productImageId: uuid })),
    /** "Tilbudsbilleder" for proposal PDFs; never shown in a shop. */
    offerImages: z.array(imageDto),
    videos: z.array(videoDto),
    categoryIds: z.array(uuid),
    /** `components`: a sampak's contents (read-only here; saved through PUT /catalogue/variants/:id/components). */
    variants: z.array(variantEditDto.extend({ id: uuid, components: z.array(z.object({ sku: z.string(), title: z.string(), quantity: z.number().int() })).optional() })),
    shops: z.array(z.object({ shopId: uuid, shopName: z.string() })),
});
export const saveProductRequest = z.object({
    number: z.string().trim().max(60).nullable().optional(),
    title: i18n,
    description: i18n.optional(),
    vendorId: uuid.nullable().optional(),
    tags: z.array(z.string().trim().max(60)).max(50).default([]),
    active: z.boolean().default(true),
    categoryIds: z.array(uuid).max(50).default([]),
    imageIds: z.array(uuid).max(30).default([]),
    /** Left out: offer images and videos stay as they are. */
    offerImageIds: z.array(uuid).max(30).optional(),
    videoIds: z.array(uuid).max(10).optional(),
    variants: z.array(variantEditDto).min(1).max(200),
});
export const productQuery = z.object({
    q: z.string().trim().max(100).optional(),
    shopId: uuid.optional(),
    categoryId: uuid.optional(),
    vendorId: uuid.optional(),
    owned: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
});
// --- storefront set-up per shop ----------------------------------------------------------------------
export const THEMES = ['christmas_2026', 'standard', 'public_webshop'];
export const theme = z.enum(THEMES);
export const THEME_NAMES = { christmas_2026: 'Christmas 2026 (gavevalg)', standard: 'Standard (firmashops)', public_webshop: 'Public webshop (B2C)' };
export const checkoutFieldMode = z.enum(['required', 'optional', 'hidden']);
export const CHECKOUT_FIELDS = ['company', 'phone', 'department', 'budget', 'ean', 'vatNumber', 'requisition', 'note', 'initials'];
export const CHECKOUT_FIELD_LABELS = { company: 'Firma', phone: 'Telefon', department: 'Afdeling', budget: 'Budget / omkostningssted', ean: 'EAN', vatNumber: 'CVR / momsnummer', requisition: 'Rekvisitionsnummer', note: 'Kommentar', initials: 'Initialer' };
export const deliveryOption = z.enum(['parcel_shop', 'home', 'business', 'office', 'pickup']);
export const shopStorefront = z.object({
    theme: theme.default('standard'),
    colors: z.object({ primary: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#1f3bb3'), accent: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#52cdff') }).default({ primary: '#1f3bb3', accent: '#52cdff' }),
    logoImageId: uuid.nullable().default(null),
    heroImageId: uuid.nullable().default(null),
    languages: z.array(language).min(1).default(['da']),
    defaultLanguage: language.default('da'),
    /** open: anyone can shop (public webshop); login: customers log in; gift_code: a code from a card opens the gift choice. */
    access: z.enum(['open', 'login', 'gift_code']).default('login'),
    selfRegistration: z.boolean().default(false),
    approvalRequired: z.boolean().default(false),
    showPrices: z.boolean().default(true),
    pricesInclVat: z.boolean().default(true),
    paymentMethods: z.array(z.enum(['bambora', 'invoice'])).default(['invoice']),
    bamboraMerchant: z.string().max(30).default(''),
    cartLimitMinor: z.number().int().min(0).nullable().default(null),
    /** Gift shops: one gift per person, grid grouped by product group, decline allowed. */
    giftGrouping: z.boolean().default(false),
    declineAllowed: z.boolean().default(false),
    deliveryOptions: z.array(deliveryOption).default(['home']),
    checkoutFields: z.record(z.string(), checkoutFieldMode).default({ company: 'optional', phone: 'optional', note: 'optional' }),
    customFields: z.array(z.object({ key: z.string().regex(/^[a-z0-9_]{1,30}$/), label: i18n, required: z.boolean().default(false), pattern: z.string().max(200).nullable().default(null) })).max(5).default([]),
    departments: z.array(z.string().max(100)).max(500).default([]),
    budgets: z.array(z.string().max(100)).max(500).default([]),
    welcome: i18n.default({}),
    footer: i18n.default({}),
    contactEmail: z.string().max(200).default(''),
    contactPhone: z.string().max(50).default(''),
    termsUrl: z.string().max(300).default(''),
    seoIndex: z.boolean().default(false),
    /** P2.4: ordering in the storefront. Off until the shop is ready to take orders here instead of in 3x. */
    checkoutEnabled: z.boolean().default(false),
    /** Orders wait for one of these to approve them by email link when approvalRequired is on. */
    approverEmails: z.array(z.string().email()).max(20).default([]),
    /** Extra text in the order confirmation mail and on the receipt. */
    confirmationText: i18n.default({}),
    /** Terms must be accepted at checkout (link from termsUrl). */
    requireTerms: z.boolean().default(true),
});
export const shopSetupDto = z.object({
    shopId: uuid,
    name: z.string(),
    slug: z.string().nullable(),
    ownedHere: z.boolean(),
    opensAt: z.string().nullable(),
    closesAt: z.string().nullable(),
    storefront: shopStorefront,
    logo: imageDto.nullable(),
    hero: imageDto.nullable(),
    previewUrl: z.string().nullable(),
    domains: z.array(z.object({ id: uuid, host: z.string(), primary: z.boolean(), tlsStatus: z.enum(['pending', 'dns', 'active', 'error']), tlsMessage: z.string().nullable(), tlsCheckedAt: z.string().nullable(), tlsExpiresAt: z.string().nullable() })),
    pages: z.array(z.object({ id: uuid, slug: z.string(), title: i18n, body: i18n, showInMenu: z.boolean(), sortOrder: z.number().int(), active: z.boolean() })),
    slides: z.array(z.object({ id: uuid, image: imageDto, title: i18n, linkUrl: z.string().nullable(), sortOrder: z.number().int(), active: z.boolean() })),
    deliveryAddresses: z.array(z.object({ id: uuid, name: z.string(), company: z.string().nullable(), department: z.string().nullable(), street1: z.string(), street2: z.string().nullable(), zip: z.string(), city: z.string(), countryCode: z.string(), customerNumber: z.string().nullable(), contact: z.string().nullable(), email: z.string().nullable(), sortOrder: z.number().int() })),
    shippingPrices: z.array(z.object({ id: uuid, method: deliveryOption, countryCode: z.string(), minOrderMinor: z.number().int(), maxWeightG: z.number().int().nullable(), priceMinor: z.number().int(), sortOrder: z.number().int() })),
});
export const saveShopSetupRequest = z.object({
    name: z.string().trim().min(1).max(200),
    slug: z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9-]{1,60}$/, 'a-z, 0-9 og bindestreg'),
    opensAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    closesAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    storefront: shopStorefront,
});
/** A sampak's components by SKU; an empty list makes the variant an ordinary one again. */
export const saveBundleComponentsRequest = z.object({ components: z.array(z.object({ sku: z.string().trim().min(1).max(100), quantity: z.number().int().min(1).max(999) })).max(50) });
export const saveDomainRequest = z.object({ host: z.string().trim().toLowerCase().regex(/^[a-z0-9.-]+\.[a-z]{2,}$/), primary: z.boolean().default(false) });
export const savePageRequest = z.object({ slug: z.string().trim().toLowerCase().regex(/^[a-z0-9-]{1,60}$/), title: i18n, body: i18n, showInMenu: z.boolean().default(true), sortOrder: z.number().int().default(100), active: z.boolean().default(true) });
export const saveSlideRequest = z.object({ imageId: uuid, title: i18n.default({}), linkUrl: z.string().trim().max(300).nullable().optional(), sortOrder: z.number().int().default(100), active: z.boolean().default(true) });
export const saveDeliveryAddressRequest = z.object({ name: z.string().trim().min(1).max(200), company: z.string().trim().max(200).nullable().optional(), department: z.string().trim().max(200).nullable().optional(), street1: z.string().trim().min(1).max(200), street2: z.string().trim().max(200).nullable().optional(), zip: z.string().trim().min(1).max(20), city: z.string().trim().min(1).max(100), countryCode: z.string().trim().length(2).default('DK'), customerNumber: z.string().trim().max(40).nullable().optional(), contact: z.string().trim().max(200).nullable().optional(), email: z.string().trim().max(200).nullable().optional(), sortOrder: z.number().int().default(100) });
export const saveShippingPriceRequest = z.object({ method: deliveryOption, countryCode: z.string().trim().length(2).default('DK'), minOrderMinor: z.number().int().min(0).default(0), maxWeightG: z.number().int().min(0).nullable().optional(), priceMinor: z.number().int().min(0), sortOrder: z.number().int().default(100) });
export const assortmentItemDto = z.object({
    productId: uuid,
    number: z.string().nullable(),
    title: z.string(),
    image: imageDto.nullable(),
    imageUrl: z.string().nullable(),
    sortOrder: z.number().int(),
    active: z.boolean(),
    groupName: z.string().nullable(),
    variants: z.array(z.object({ variantId: uuid, sku: z.string(), title: z.string(), basePriceMinor: z.number().int().nullable(), shopPriceMinor: z.number().int().nullable() })),
});
export const saveAssortmentRequest = z.object({
    items: z.array(z.object({ productId: uuid, sortOrder: z.number().int().default(100), active: z.boolean().default(true), groupName: z.string().trim().max(100).nullable().optional(), prices: z.array(z.object({ variantId: uuid, priceMinor: z.number().int().min(0).nullable() })).default([]) })).max(5000),
});
/** docs/design/12 section 6: a showcase copy of a shop whose orders stay inside the system. */
export const demoCopyRequest = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    slug: z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9-]{1,60}$/, 'a-z, 0-9 og bindestreg').optional(),
    /** A host for the copy; one that belongs to the source shop moves to the copy. */
    host: z.string().trim().toLowerCase().regex(/^[a-z0-9.-]+\.[a-z]{2,}$/).optional(),
    /** Demo mails go only here (the presenter); empty keeps them in the outbox. */
    mailTo: z.string().trim().email().nullable().optional(),
    logins: z.number().int().min(1).max(5).default(2),
});
export const demoCopyResultDto = z.object({
    shopId: uuid,
    name: z.string(),
    slug: z.string(),
    url: z.string(),
    logins: z.array(z.object({ email: z.string().nullable(), giftCode: z.string().nullable(), password: z.string() })),
});
export const demoResetResultDto = z.object({ cancelledOrders: z.number().int(), toppedUp: z.number().int() });
//# sourceMappingURL=shopfront.js.map